import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { ConfigConstants } from '../../config/config-constants';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { WindowService } from '../../services/window/window.service';
import { EncryptionService } from '@evorait/pace-encryption';

@Injectable({
    providedIn: 'root',
})
export class LocalStorageCommonService {
    private localStorage: Storage;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly window: WindowService,
        private readonly encryption: EncryptionService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {
        this.localStorage = this.window.nativeWindow.localStorage;
    }

    /**
     * Central function to access the local storage. Any write actions on the local storage should use this function.
     * This enables us to replace the localStorage by a different data storage provider such as any database.
     *
     * @param id the identifier used as key to store the value in localStorage
     * @param value the value to be stored in localStorage
     */
    public storageSet(id: string, value: unknown): void {
        let data = JSON.stringify(value);
        if (
            this.clientManager.isPaceFormsFI() ||
            this.clientManager.isPacePWA() ||
            this.clientManager.isPaceFormsForAuthors()
        ) {
            data = this.encryption.encrypt(data);
        }
        return this.localStorage.setItem(id, data);
    }

    /**
     * This function is only to be used to set some simple, unencrypted values before initialization. Only use if absolutely required!
     * Normal request to localStorage should use the `storageSet` function.
     *
     * @param id the identifier used as key to store the value in localStorage
     * @param value the value as string to be stored in localStorage
     */
    public preInitializationStorageSet(id: string, value: string): void {
        this.localStorage.setItem(id, value);
    }

    /**
     * Central function to access the local storage. Any read actions on the local storage should use this function.
     * This enables us to replace the localStorage by a different data storage provider such as any database.
     *
     * If the desired value does not exist, the value from localStorage will be 'null'. This is subsequently changed to
     * 'undefined' to keep 'null' out of the codebase.
     *
     * If the desired value was found, it will be returned with type annotation 'unknown'. This means that any access to
     * any property will only be possible after the existence of that property was ensured by a suitable check in the
     * code.
     *
     * @param id
     */
    public storageGet<T>(id: string): T | undefined {
        let value = this.localStorage.getItem(id);
        if (
            (this.clientManager.isPaceFormsFI() ||
                this.clientManager.isPacePWA() ||
                this.clientManager.isPaceFormsForAuthors()) &&
            value
        ) {
            value = this.encryption.decrypt(value);
        }

        return value === null ? undefined : <T>JSON.parse(value);
    }

    /**
     * This function is only to be used to read some simple, unencrypted values before initialization. Only use if absolutely required!
     * Normal request to localStorage should use the `storageSet` function.
     *
     * @param id the identifier used as key to store the value in localStorage
     */
    public preInitializationStorageGet(id: string): string | null {
        return this.localStorage.getItem(id);
    }

    /**
     * Central function to access the local storage. Any read actions on the local storage should use this function.
     * This enables us to replace the localStorage by a different data storage provider such as any database.
     *
     * @param id
     */
    public storageRemove(id: string): void {
        return this.localStorage.removeItem(id);
    }

    /**
     * Return all available keys in the local storage. Any object stored within this local storage instance will be
     * listed here with its key.
     */
    public storageKeys(): string[] {
        return Object.keys(this.localStorage);
    }

    public makeId(id: NotificationIdentifierModel): string {
        const constSeparator = this.clientManager.isWindows()
            ? ConfigConstants.windowsFileNameSeparator
            : ConfigConstants.unixFileNameSeparator;

        return id.getFrontId() + constSeparator + id.getQmnum() + constSeparator + id.getManum();
    }

    /**
     * Sums up the size of all keys currently stored in localStorage
     * To read the correct local storage memory size, we are reading out the raw strings from local storage.
     * It is on purpose that we are not using the storageGet() function of this service, because it would decrypt and
     * parse JSON objects. That process would change the storage size.
     */
    public printConsumedLocalStorageSize(): void {
        const kilobyteSize = 1024;
        const utf16Factor = 2;
        const maxStringSize = 50;
        const numberOfDigits = 2;
        const totalSize = this.storageKeys()
            .map((key) => {
                const value = this.localStorage.getItem(key);
                const retrievedValueLength = value !== null ? value.length : 0;
                const length = (key.length + retrievedValueLength) * utf16Factor;
                this.log.debug(
                    `${key.substring(0, maxStringSize)}: ${(length / kilobyteSize).toFixed(numberOfDigits)} KiB`,
                );
                return length;
            })
            .reduce((acc, length) => acc + length, 0);
        this.log.debug(`Total Size: ${(totalSize / kilobyteSize).toFixed(numberOfDigits)} KiB`);
    }
}
